import {
  createLightTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';

// Brand palette derived from the actual deployed APIM Developer Portal CMS theme.
// Primary accent (buttons, hover): #FF5F00 (orange)
// Link color: #2288CE (blue) — applied separately via global CSS
const portalBrand: BrandVariants = {
  10: '#331300',
  20: '#4D1D00',
  30: '#662600',
  40: '#803000',
  50: '#993A00',
  60: '#B34400',
  70: '#CC4E00',
  80: '#FF5F00',
  90: '#FF7726',
  100: '#FF8F4D',
  110: '#FFA773',
  120: '#FFBF99',
  130: '#FFD1B3',
  140: '#FFDFC6',
  150: '#FFEDDB',
  160: '#FFF6ED',
};

export const lightTheme: Theme = {
  ...createLightTheme(portalBrand),
  // Override tokens to match the APIM Developer Portal CMS theme
  fontFamilyBase: '"Open Sans", sans-serif',
  fontFamilyMonospace:
    'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  colorNeutralForeground1: '#000000',
  colorNeutralForeground2: '#2A2A2A',
  colorNeutralForeground3: '#7F7F7F',
  colorNeutralBackground2: '#fafafa',
  colorNeutralStroke1: '#D9D9D9',
  colorNeutralStroke2: '#F1F3F4',
};

// -----------------------------------------------------------------------------
// Portal-scoped derived constants
// -----------------------------------------------------------------------------
//
// Token-driven theming covers the majority of color/font surfaces, but the
// portal reproduces a legacy APIM CMS visual treatment that uses brand-scoped
// gradient stops, drop shadows, and an explicit base unit. Centralizing these
// values keeps the surface deterministic and prevents ad-hoc literals from
// re-appearing in `makeStyles` blocks.

/** Portal layout base unit. Multiply for paddings, gaps, and breakpoints. */
export const portalSpacing = {
  xxxs: '2px',
  xxs: '4px',
  xs: '5px',
  s: '8px',
  sNudge: '10px',
  m: '12px',
  l: '14px',
  xl: '15px',
  xxl: '16px',
  xxxl: '20px',
  hero: '24px',
  page: '30px',
  pageWide: '32px',
  heroMobile: '40px',
  navHeight: '50px',
  watermarkOffset: '64px',
} as const;

/** Portal typography sizes used in legacy-themed surfaces. */
export const portalTypography = {
  navLink: '13px',
  navLinkLineHeight: '50px',
  docs: '16px',
  tagline: '14px',
  body: '13px',
  heroH1: '30px',
  heroBody: '25px',
  ctaButton: '16px',
  codeBlock: '13px',
  stepNumber: '14px',
  fontFamilyBase: '"Open Sans", sans-serif',
  fontFamilyMono:
    "'Cascadia Code', 'Fira Code', Consolas, monospace",
  weightRegular: '400',
  weightSemibold: '600',
} as const;

/** Portal-specific brand-derived colors used by the legacy APIM CMS treatment. */
export const portalColors = {
  /** Primary brand orange. */
  brand: portalBrand[80],
  /** Brand hover state (darkened ~10%). */
  brandHover: '#E65000',
  /** Soft cream gradient stop for the navigation background. */
  navGradientPeach: '#FDCEB4',
  /** Navigation border under-line. */
  navBorder: '#B3B3B3',
  /** Muted gray for secondary text in legacy surfaces. */
  mutedText: '#7F7F7F',
  /** Default text in legacy surfaces. */
  bodyText: '#000000',
  /** Hero supporting copy. */
  heroBodyText: '#212529',
  /** Resource card hover accent (orange blue companion). */
  resourceCardAccent: '#2288CE',
  /** Resource card background tint. */
  resourceCardIconBg: '#FDF4EF',
  /** App shell background. */
  appShellBg: '#FFFFFF',
  /** Code block background. */
  codeBlockBg: '#F5F5F5',
  /** Light divider. */
  divider: '#E0E0E0',
  /** Sign-out / signOut hover state. */
  signOutText: '#FFFFFF',
} as const;

/** Box-shadow definitions used by the legacy resource cards. */
export const portalShadows = {
  resourceCard: '0px 0px 2px 2px rgba(0, 0, 0, 0.18)',
  resourceCardHover: '0px 0px 4px 2px rgba(255, 95, 0, 0.30)',
  mobileMenu: '2px 2px 10px 0px rgba(0, 0, 0, 0.50)',
} as const;

/** Portal gradient backgrounds reproducing the legacy navigation treatment. */
export const portalGradients = {
  // Opaque stops only. The nav bar and the mobile menu are the sole consumers,
  // and both sit at the top of the page over scrolling content — a translucent
  // stop let page content bleed through the open mobile menu. #FEE7DA is the
  // prior rgba(253, 206, 180, 0.49) middle stop composited over white, so the
  // peach appearance is preserved while the surface is now fully opaque.
  navBackground:
    'linear-gradient(#FFFFFF 0%, #FEE7DA, #FDCEB4)',
} as const;

/** Watermark opacity reused on the hero. */
export const portalOpacity = {
  watermark: 0.08,
} as const;

/** Breakpoint constants — `@media (max-width: 768px)` is the portal's only breakpoint. */
export const portalBreakpoints = {
  mobile: '768px',
} as const;

/** Page-level layout widths. */
export const portalLayout = {
  /**
   * Reading-page content width: tracks the viewport (92vw) on ordinary screens
   * and caps at 70rem so prose line length stays readable on wide monitors.
   * Pure CSS (no resize listeners); rem-based so the cap scales with the
   * user's browser font-size preference.
   */
  docPageMaxWidth: 'min(70rem, 92vw)',
} as const;
